import { animate, style, transition, trigger } from '@angular/animations';
import { AsyncPipe } from '@angular/common';
import {
  AfterContentInit,
  Component,
  DOCUMENT,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { BurgerMenuContent, CategoryBurgerLink, Image, Link, LinkContent, StoreType, SubNavigationContent } from 'src/app/models';
import { BasketQuery } from 'src/app/services/basket/basket.query';
import { BasketService } from 'src/app/services/basket/basket.service';
import { CategoryService } from 'src/app/services/category/category.service';
import { ContentService, headerSections, sections } from 'src/app/services/content/content.service';
import { ShippingDialogService } from 'src/app/services/dialog/shipping-dialog.service';
import { MobileService } from 'src/app/services/mobile.service';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { environment } from 'src/environments/environment';
import { LayoutService } from 'src/app/services/device-detector/layout.service';
import { buttonIcons } from '../../../modules/icon.module';
import { IconButtonComponent } from '../../components';
import { MemberBarComponent } from '../../components/auth';
import { ContentImagePipe } from '../../pipes';
import { ContentSection } from '../content-section';

@Component({
  selector: 'app-navigation-header',
  templateUrl: './navigation-header.component.html',
  styleUrls: ['./navigation-header.component.scss'],
  animations: [
    trigger('installAnimation', [
      transition(':enter', [style({ opacity: 0, width: 0 }), animate('800ms ease-out', style({ opacity: 1, width: '*' }))]),
      transition(':leave', [style({ opacity: 1, width: '*' }), animate('800ms ease-in', style({ opacity: 0, width: 0 }))]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterLink, MemberBarComponent, ContentImagePipe, IconButtonComponent, AsyncPipe, MatBadgeModule],
})
export class NavigationHeaderComponent extends ContentSection implements OnInit, AfterContentInit, OnDestroy {
  private basketService = inject(BasketService);
  private basketQuery = inject(BasketQuery);
  private shippingDialogService = inject(ShippingDialogService);
  private mobileService = inject(MobileService);
  private themeService = inject(ThemeService);
  private categoryService = inject(CategoryService);
  private layoutService = inject(LayoutService);
  private elRef = inject(ElementRef);
  private router = inject(Router);
  private document = inject<Document>(DOCUMENT);
  private ngZone = inject(NgZone);

  subscription: Subscription;

  merchantContentUrl = `${environment.urls.content.merchant_content}/`;
  contactUsLink = `/${environment.urls.content.contact_us}`;
  filterIcon = buttonIcons.filter;

  @Input() showFilter: boolean;
  @ViewChild('tooltipRef') tooltipRef: ElementRef;
  @HostBinding('class.hide-border') hideBorder = false;
  @HostBinding('class.sticky') sticky = false;

  hideGroceries: boolean;
  groceries: Link;
  searchPlaceholder = '';
  searchButton = '';

  showTooltip = false;

  links: (LinkContent | SubNavigationContent | BurgerMenuContent | CategoryBurgerLink)[];
  headerLinks: LinkContent[];
  openNavigationName?: string;
  openNavigationRef: HTMLElement;
  closeSubNavTimeout: any;

  storeLogo: Image;

  @Input() showBasket = true;
  basketIcon = buttonIcons.basket;
  count$: Observable<number>;

  @Input() showShipping = true;
  isMobile = false;
  menuIcon: string;
  storeClosed: boolean;
  menuLabel: string;

  enableMembers: boolean;
  showOnlyLogo: boolean;
  logoLink: string;

  @HostListener('focusout', ['$event']) onFocusOut(event: FocusEvent) {
    if (!this.openNavigationRef?.contains(event.relatedTarget as any) && this.document.hasFocus()) {
      this.closeNavigation();
    }
  }

  @HostListener('mouseenter', ['$event']) onMouseEnter(event: MouseEvent) {
    clearTimeout(this.closeSubNavTimeout);
  }

  @HostListener('mouseleave', ['$event']) onMouseLeave(event: MouseEvent) {
    this.ngZone.runOutsideAngular(() => {
      this.closeSubNavTimeout = setTimeout(() => {
        this.closeNavigation();
      }, 2000);
    });
  }

  constructor() {
    const contentService = inject(ContentService);
    super(contentService, sections.header);

    this.enableMembers = this.contentService.getMerchant().enableMembers;

    const buttonStyle = this.content.get('groceriesButton');
    this.hideGroceries = this.content.get('hideGroceries');
    this.groceries = { url: environment.urls.products, name: this.content.get('groceries') || 'groceries', button: buttonStyle };
    this.searchPlaceholder = this.content.get('searchPlaceholder') || 'searchPlaceholder';
    this.searchButton = this.content.get('searchButtonText') || 'searchButtonText';

    this.storeClosed = this.contentService.getStoreClosed();
    this.menuLabel = this.content.get('deliveryPickup');

    this.storeLogo = { src: this.content.get('headerImage'), alt: this.contentService.getTitle() };
    this.sticky = this.content.get('sticky');

    this.checkScreenSize();

    // TODO: add accessability
    const { header, navigation } = headerSections;

    const headerContent = this.contentService.getMerchantContent(header);
    this.headerLinks = [];
    for (const entry of headerContent) {
      switch (entry.type) {
        case 'simple': {
          const link = this.contentService.getContentLink(entry);
          this.headerLinks.push(link);
          break;
        }
        case 'link':
          this.headerLinks.push(entry);
          break;
      }
    }

    const navigationContent = this.contentService.getMerchantContent(navigation);
    this.links = [];
    for (const entry of navigationContent) {
      switch (entry.type) {
        case 'simple': {
          const link = this.contentService.getContentLink(entry);
          this.links.push(link);
          break;
        }
        case 'categoryBurger': {
          const link = this.categoryService.buildCategoryBurgerMenu(entry);
          if (link) this.links.push(link);
          break;
        }
        case 'link':
        case 'subNav':
        case 'burger':
          this.links.push(entry);
          break;
      }
    }

    const merchant = this.contentService.getMerchant();
    // CL Party Service Global shop
    this.showOnlyLogo = merchant.id === 169;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkScreenSize();
  }

  checkScreenSize() {
    switch (true) {
      case this.layoutService.isMobile:
      case this.layoutService.isTablet:
        this.menuIcon = buttonIcons.menu;
        this.isMobile = true;
        break;
      default:
        this.menuIcon = buttonIcons.shipping;
        this.isMobile = false;
    }
  }

  ngOnInit() {
    this.subscription = new Subscription();

    this.count$ = this.basketQuery.selectProductLines$.pipe(
      map((productLines) => {
        let count = 0;
        productLines.forEach((line) => (count += line.quantity));
        return count;
      }),
    );

    if (this.router.url === '/') {
      this.hideBorder = true;
    }

    this.subscription.add(
      this.router.events.subscribe((event) => {
        switch (true) {
          case event instanceof NavigationEnd && event.url === '/':
            this.hideBorder = true;
            break;
          default:
            this.hideBorder = false;
        }
      }),
    );
  }

  ngAfterContentInit() {
    this.themeService.subscribeComponent(this.elRef);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  openTooltip() {
    this.showTooltip = true;
    this.tooltipRef.nativeElement.focus();
  }

  openNavigation(el: HTMLElement, navigation: string) {
    if (this.openNavigationRef) {
      this.openNavigationRef.blur();
    }

    this.openNavigationName = navigation;
    this.openNavigationRef = el;

    this.ngZone.runOutsideAngular(() => setTimeout(() => el.focus(), 0));
  }

  closeNavigation() {
    this.openNavigationName = undefined;
  }

  closeTooltip() {
    this.showTooltip = false;
  }

  sameSite(link: LinkContent) {
    return this.contentService.sameSite(link);
  }

  openMenu() {
    if (this.isMobile) {
      this.mobileService.setOverlay(true);

      return;
    }

    this.shippingDialogService.openShippingDialog();
  }

  openBasket() {
    this.basketService.setOverlay(true);
  }
}
